import { useMemo, useState } from 'react'
import { router, useForm } from '@inertiajs/react'
import { Link2, Unlink } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { TokenUsageCopy, TokenUsagePerson, TokenUsagePersonIdentifier, TokenUsageReport } from '@/frontend/pages/Dashboard/DigitalWorkers/types'
import UsageChannelIcon from './UsageChannelIcon'

interface Props {
  workerId: string
  person: TokenUsagePerson
  canonicalPeople: TokenUsageReport['canonical_people']
  copy: TokenUsageCopy
  onClose: () => void
}

export default function MappedPersonEditor({ workerId, person, canonicalPeople, copy, onClose }: Props) {
  const renameForm = useForm({ person: { name: person.name } })
  const mergeForm = useForm({ person: { target_person_id: '' } })
  const [unlinking, setUnlinking] = useState<TokenUsagePersonIdentifier | null>(null)
  const [detaching, setDetaching] = useState(false)
  const [confirmingMerge, setConfirmingMerge] = useState(false)
  const otherPeople = useMemo(
    () => canonicalPeople.filter((item) => item.id !== person.person_id),
    [canonicalPeople, person.person_id],
  )

  const saveName = () => {
    renameForm.patch(`/dashboard/digital_workers/${workerId}/token_usage_people/${person.person_id}`, {
      preserveScroll: true,
      onSuccess: onClose,
    })
  }

  const mergePeople = () => {
    mergeForm.post(`/dashboard/digital_workers/${workerId}/token_usage_people/${person.person_id}/merge`, {
      preserveScroll: true,
      onSuccess: onClose,
    })
  }

  const detachIdentity = () => {
    if (!unlinking?.identifier_id || !person.person_id) return

    router.delete(
      `/dashboard/digital_workers/${workerId}/token_usage_people/${person.person_id}/identifiers/${unlinking.identifier_id}`,
      {
        preserveScroll: true,
        onStart: () => setDetaching(true),
        onFinish: () => setDetaching(false),
        onSuccess: onClose,
      },
    )
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-[16px]">{copy.people.dialog_title}</DialogTitle>
        <DialogDescription className="text-[13px]">{copy.people.dialog_description}</DialogDescription>
      </DialogHeader>

      <div className="space-y-5 py-1">
        <div className="space-y-2">
          <Label htmlFor="token-usage-person-name">{copy.people.alias_label}</Label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              id="token-usage-person-name"
              value={renameForm.data.person.name}
              onChange={(event) => renameForm.setData('person', { name: event.target.value })}
            />
            <Button
              type="button"
              variant="outline"
              onClick={saveName}
              disabled={renameForm.processing || !renameForm.data.person.name.trim() || renameForm.data.person.name.trim() === person.name}
            >
              {copy.people.save_name}
            </Button>
          </div>
        </div>

        <section className="space-y-2.5 border-t border-border pt-4">
          <div>
            <h3 className="text-[12px] font-medium text-foreground">{copy.people.identities_title}</h3>
            <p className="mt-0.5 text-[11px] text-muted-foreground">{copy.people.identities_description}</p>
          </div>
          <div className="overflow-hidden rounded-lg border border-border">
            {person.identifiers.map((identifier) => (
              <div key={`${identifier.channel}:${identifier.person_ref_hash}`} className="flex items-center justify-between gap-3 border-b border-border px-3 py-2.5 last:border-b-0">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="gap-1 text-[10px] font-medium">
                      <UsageChannelIcon channel={identifier.channel} />
                      {identifier.channel_label}
                    </Badge>
                    <span className="truncate text-[12px] text-foreground">{identifier.source_name || person.name}</span>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="shrink-0 text-[11px] text-muted-foreground hover:text-destructive"
                  onClick={() => setUnlinking(identifier)}
                >
                  <Unlink className="size-3.5" />
                  {copy.people.unlink}
                </Button>
              </div>
            ))}
          </div>
        </section>

        {otherPeople.length > 0 && (
          <section className="space-y-2.5 border-t border-border pt-4">
            <Label htmlFor="token-usage-person-merge">{copy.people.merge_label}</Label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Select value={mergeForm.data.person.target_person_id} onValueChange={(value) => mergeForm.setData('person', { target_person_id: value })}>
                <SelectTrigger id="token-usage-person-merge" className="w-full min-w-0 flex-1">
                  <SelectValue placeholder={copy.people.merge_placeholder} />
                </SelectTrigger>
                <SelectContent>
                  {otherPeople.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      <span className="flex min-w-0 flex-1 items-center justify-between gap-4">
                        <span className="truncate">{item.name}</span>
                        {item.channels.length > 0 && (
                          <span className="flex shrink-0 items-center gap-1.5 text-[11px] text-muted-foreground">
                            {item.channels.map((channel) => (
                              <span key={channel} className="inline-flex items-center gap-1">
                                <UsageChannelIcon channel={channel} />
                                {channel}
                              </span>
                            ))}
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" disabled={!mergeForm.data.person.target_person_id || mergeForm.processing} onClick={() => setConfirmingMerge(true)}>
                <Link2 className="size-3.5" />
                {copy.people.merge_action}
              </Button>
            </div>
            <p className="text-[11px] leading-relaxed text-muted-foreground">{copy.people.merge_help}</p>
          </section>
        )}
      </div>

      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose}>{copy.people.done}</Button>
      </DialogFooter>

      <AlertDialog open={Boolean(unlinking)} onOpenChange={(open) => { if (!open) setUnlinking(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{copy.people.unlink_title}</AlertDialogTitle>
            <AlertDialogDescription>
              {copy.people.unlink_description.replace('%{channel}', unlinking?.channel_label ?? '')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={detaching}>{copy.people.cancel}</AlertDialogCancel>
            <AlertDialogAction onClick={detachIdentity} disabled={detaching}>{copy.people.confirm_unlink}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmingMerge} onOpenChange={setConfirmingMerge}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{copy.people.merge_title}</AlertDialogTitle>
            <AlertDialogDescription>{copy.people.merge_description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{copy.people.cancel}</AlertDialogCancel>
            <AlertDialogAction onClick={mergePeople}>{copy.people.confirm_merge}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
